import { useQuery, useQueryClient } from 'react-query';
import { useEffect } from 'react';
import { suiClient, getActiveValidators, type ActiveValidator } from '@/lib/suiClient';
import { generateMockGovernanceVote, generateMockValidatorSnapshot } from '@/data/mockVoteData';
import type { GovernanceVoting, ValidatorSnapshot } from '@/types/governance';

// Create a simple hash of validator addresses to use as cache key
function getValidatorCacheKey(validators: ActiveValidator[]): string {
    if (validators.length === 0) return 'empty';
    // Use first few validator addresses to create a stable cache key
    return validators.slice(0, 3).map(v => v.address.slice(0, 10)).join('-');
}

// Check if we're using mock data and get the scenario
function getMockScenario(): { usingMock: boolean; scenario: string } {
    const objectId = import.meta.env.VITE_GOVERNANCE_VOTING_OBJECT_ID;

    if (!objectId || objectId === 'mock') {
        return { usingMock: true, scenario: 'mock_beforecutoff' }; // Default scenario
    }

    // Check for specific mock scenarios
    const mockScenarios = [
        'mock_notstarted',
        'mock_beforecutoff',
        'mock_aftercutoff',
        'mock_ended',
        'mock_passing',
        'mock_notpassingbutyes',
        'mock_notpassingbutno',
        'mock_early_passing_voting',
        'mock_early_passing_staking'
    ];

    if (mockScenarios.includes(objectId)) {
        return { usingMock: true, scenario: objectId };
    }

    return { usingMock: false, scenario: '' };
}

// Parse governance voting object from SUI RPC response
function parseGovernanceVotingObject(response: any): GovernanceVoting {
    const content = response.data?.content;
    if (!content || content.dataType !== 'moveObject') {
        throw new Error('Invalid governance voting object: not a moveObject or no content');
    }

    const fields = content.fields as Record<string, any>;
    return {
        id: fields.id.id,
        result: fields.result.variant, // Extract variant field instead of whole result object
        description: fields.description,
        digests: fields.digests?.fields?.contents || [],
        start_timestamp_ms: +fields.start_timestamp_ms,
        minimum_duration_timestamp_ms: +fields.minimum_duration_timestamp_ms,
        end_timestamp_ms: +fields.end_timestamp_ms,
        votes: fields.votes?.fields?.contents?.reduce(
            (acc: Record<string, 'Yes' | 'No' | 'Abstain'>, curr: any) => {
                acc[curr.fields.key] = curr.fields.value;
                return acc;
            },
            {} as Record<string, 'Yes' | 'No' | 'Abstain'>
        ) || {}
    };
}

// Parse validator snapshot object from SUI RPC response
function parseValidatorSnapshotObject(response: any): ValidatorSnapshot {
    const content = response.data?.content;
    if (!content || content.dataType !== 'moveObject') {
        throw new Error('Invalid validator snapshot object: not a moveObject or no content');
    }

    const fields = content.fields as Record<string, any>;
    return {
        id: fields.id.id,
        all_stake: fields.all_stake?.fields?.contents?.reduce(
            (acc: Record<string, bigint>, curr: any) => {
                acc[curr.fields.key] = BigInt(curr.fields.value);
                return acc;
            },
            {} as Record<string, bigint>
        ) || {},
        ignored_stake: fields.ignored_stake?.fields?.contents?.reduce(
            (acc: Record<string, bigint>, curr: any) => {
                acc[curr.fields.key] = BigInt(curr.fields.value);
                return acc;
            },
            {} as Record<string, bigint>
        ) || {}
    };
}

export function useActiveValidators() {
    return useQuery(
        ['active-validators'],
        async () => {
            const validators = await getActiveValidators();
            if (validators.length === 0) {
                console.warn('No active validators found or error fetching them.');
                return [];
            }
            return validators;
        },
        {
            refetchInterval: 600000, // 10 minutes (validator set changes infrequently)
            staleTime: 480000, // 8 minutes
            refetchOnWindowFocus: false,
            suspense: false,
            keepPreviousData: true,
            refetchIntervalInBackground: true
        }
    );
}

export function useGovernanceVote() {
    const { data: activeValidators = [] } = useActiveValidators();

    const validatorKey = getValidatorCacheKey(activeValidators);
    const queryClient = useQueryClient();

    const { usingMock, scenario } = getMockScenario();
    const objectId = import.meta.env.VITE_GOVERNANCE_VOTING_OBJECT_ID;

    const query = useQuery(
        ['governance-vote', validatorKey, usingMock, scenario], // Include scenario in key
        async () => {
            if (usingMock) {
                // Generate mock vote based on scenario and real validators if available
                return generateMockGovernanceVote(scenario, activeValidators);
            }

            const response = await suiClient.getObject({
                id: objectId!,
                options: { showContent: true }
            });

            // Parse the response and transform to our interface
            console.log('governance vote response', response);
            const parsed = parseGovernanceVotingObject(response);
            console.log('parsed governance vote response', parsed);
            return parsed;
        },
        {
            refetchInterval: 30000, // 30 seconds
            staleTime: 25000,
            refetchOnWindowFocus: false,
            suspense: false,
            keepPreviousData: false, // Allow UI to update when query key changes
            refetchIntervalInBackground: true,
            enabled: !usingMock ? true : activeValidators.length > 0 // Wait for validators in mock mode
        }
    );

    // If we ran once with empty validators, invalidate when real ones arrive
    useEffect(() => {
        if (usingMock && activeValidators.length > 0) {
            queryClient.invalidateQueries(['governance-vote', 'empty', true]);
        }
    }, [activeValidators.length, usingMock, queryClient]);

    return query;
}

export function useValidatorSnapshot() {
    const { data: activeValidators = [] } = useActiveValidators();

    const validatorKey = getValidatorCacheKey(activeValidators);
    const queryClient = useQueryClient();

    const { usingMock, scenario } = getMockScenario();
    const snapshotObjectId = import.meta.env.VITE_VALIDATOR_SNAPSHOT_OBJECT_ID;

    const query = useQuery(
        ['validator-snapshot', validatorKey, usingMock, scenario], // Include scenario in key
        async () => {
            if (usingMock) {
                // Generate mock snapshot based on scenario and real validators if available
                return generateMockValidatorSnapshot(scenario, activeValidators);
            }

            const response = await suiClient.getObject({
                id: snapshotObjectId!,
                options: { showContent: true }
            });

            console.log('validator snapshot response', response);
            const parsed = parseValidatorSnapshotObject(response);
            console.log('parsed validator snapshot response', parsed);
            return parsed;
        },
        {
            refetchInterval: 300000, // 5 minutes (stake changes less frequently)
            staleTime: 240000,
            refetchOnWindowFocus: false,
            suspense: false,
            keepPreviousData: false, // Allow UI to update when query key changes
            refetchIntervalInBackground: true,
            enabled: !usingMock ? true : activeValidators.length > 0 // Wait for validators in mock mode
        }
    );

    // If we ran once with empty validators, invalidate when real ones arrive
    useEffect(() => {
        if (usingMock && activeValidators.length > 0) {
            queryClient.invalidateQueries(['validator-snapshot', 'empty', true]);
        }
    }, [activeValidators.length, usingMock, queryClient]);

    return query;
} 